import React, { useState, useContext, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { prefetchOrder } from "../../hooks/useOrder";
import { UserContext } from "../../context/userContext";
import * as OrdersPageAnalytics from "../../services/analytics/OrdersPageAnalytics";
import MotionChevronRight from "../../Icons/MotionChevronRight";
import UiDateUtils from "../../../utils/UiDateUtils";
import CapitalizeEachWord from "../../../utils/CapitalizeEachWord";
import { formatPurchaseRequestEta } from "../../../utils/purchaseRequestEta";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Tooltip,
  IconButton,
  Box,
} from "@mui/material";
import { tooltipClasses } from "@mui/material/Tooltip";
import MotionCopyIcon from "../../Icons/MotionCopyIcon";
import { styled } from "@mui/material/styles";
import { getMarketBrandPath } from "../../../utils/marketBrandUtils";
import HasPermissionTo, {
  hasPermissionTo,
} from "../../../common/HasPermissionTo";
import {
  isCanadianPreorderUser,
  usesDealerExperienceRoutes,
} from "../../../utils/canadianPreorderExperience";

const orderRowPropType = PropTypes.shape({
  label: "Customer Name",
  orderIdString: PropTypes.string.isRequired,
  orderDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]),
  customerName: PropTypes.string,
  vin: PropTypes.string,
  source: PropTypes.string,
  orderStatus: PropTypes.string,
  orderType: PropTypes.string,
  businessType: PropTypes.string,
  delivery: PropTypes.string,
  estimatedDeliveryDate: PropTypes.string,
});

const LightTooltipBase = ({ className, ...props }) => (
  <Tooltip
    {...props}
    classes={{ popper: className }}
    data-testid="copied-tooltip"
  />
);

LightTooltipBase.propTypes = {
  className: PropTypes.string,
};

LightTooltipBase.defaultProps = {
  className: "",
};

const LightTooltip = styled(LightTooltipBase)(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[1],
    border: "1px solid #808080",
    borderRadius: "4px",
    padding: "4px",
  },
}));

const CopyableCell = React.memo(function CopyableCell({
  value,
  rowId,
  columnId,
  isCopied,
  isLincoln,
  onCopy,
}) {
  const handleClick = () => {
    onCopy(value, rowId, columnId);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 2,
      }}
    >
      <Typography variant="body2" component="p">
        {value}
      </Typography>

      <LightTooltip
        title={
          <Typography variant="body2">
            {isCopied ? "Copied to clipboard" : "Copy"}
          </Typography>
        }
        placement="top"
      >
        <IconButton
          data-testid="copy-button"
          aria-label={`Copy ${columnId === "vin" ? "VIN" : "order number"} ${value}`}
          disableRipple
          onClick={handleClick}
          sx={{
            padding: 0,
            cursor: "pointer",
            width: "16px",
            height: "16px",
            flexShrink: 0,
            position: "relative",
            transition: "all 0.2s ease-in-out",
            "& svg": {
              position: "absolute",
              top: 0,
              left: 0,
              transition: "all 0.1s ease-in-out",
              opacity: 1,
              transform: "scale(1)",
            },
          }}
        >
          <MotionCopyIcon
            htmlColor={isCopied ? "green" : "#666666"}
            sx={isLincoln ? { width: "16px", height: "16px" } : undefined}
          />
        </IconButton>
      </LightTooltip>
    </Box>
  );
});

CopyableCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  rowId: PropTypes.string.isRequired,
  columnId: PropTypes.string.isRequired,
  isCopied: PropTypes.bool.isRequired,
  isLincoln: PropTypes.bool.isRequired,
  onCopy: PropTypes.func.isRequired,
};

const LinkToDetails = React.memo(function LinkToDetails({
  orderIdString,
  queryClient,
  commonId,
  user,
  isPreOrder,
  viewLabel,
}) {
  const dealerRoutes = usesDealerExperienceRoutes(user);
  const linkLabel = viewLabel || (user?.brand === "L" ? "View" : null);
  const linkUrl = useMemo(
    () =>
      dealerRoutes
        ? getMarketBrandPath(
            user.market,
            user.brand,
            `/customer-handling/${orderIdString}`
          )
        : `/customer-handling/${orderIdString}`,
    [orderIdString, user?.brand, user?.market, dealerRoutes]
  );

  const handlePrefetch = useCallback(() => {
    prefetchOrder(queryClient, commonId, orderIdString, user);
  }, [queryClient, commonId, orderIdString, user]);

  return (
    <Link
      to={{
        pathname: linkUrl,
        state: isPreOrder ? { mockPreOrder: true } : undefined,
      }}
      onMouseDown={isPreOrder ? undefined : handlePrefetch}
      aria-label={linkLabel || "Details"}
      style={{ display: "inline-flex", alignItems: "center" }}
    >
      {linkLabel || (
        <MotionChevronRight
          data-testid="details-arrow-icon"
          sx={{ color: "#066fef", fontSize: "20px" }}
        />
      )}
    </Link>
  );
});

LinkToDetails.propTypes = {
  orderIdString: PropTypes.string.isRequired,
  queryClient: PropTypes.shape({}).isRequired,
  commonId: PropTypes.string.isRequired,
  user: PropTypes.shape({}),
  isPreOrder: PropTypes.bool,
  viewLabel: PropTypes.string,
};

LinkToDetails.defaultProps = {
  user: null,
  isPreOrder: false,
  viewLabel: undefined,
};

const STATUS_STYLES = {
  accepted: { color: "#008200", bgcolor: "#E6F4EA" },
  created: { color: "#066fef", bgcolor: "#E7F1FE" },
  "created-action": { color: "#D62D14", bgcolor: "#FDECEA" },
  canceled: { color: "#4d4d4d", bgcolor: "#EDEDED" },
};

// Tighter side padding leaves room for the larger desktop text
const LINCOLN_CELL_PADDING = { py: 4, px: 3 };

// Relative widths measured from the 1200px Client Handling reference table.
const FIXED_COLUMN_WIDTHS = {
  orderStatus: 165,
  orderIdString: 235,
  orderDate: 170,
  customerName: 200,
  vin: 330,
  delivery: 170,
  actions: 100,
};

const PRE_ORDER_TYPE = "PREORDER";
const PRE_ORDER_NO_VIN_TEXT = "Preorder";

const STATUS_LABELS = {
  created: "Created",
  accepted: "Accepted",
  confirmed: "Accepted",
  canceled: "Canceled",
  cancelled: "Canceled",
};

const mapStatusLabel = (status) =>
  STATUS_LABELS[(status || "").toLowerCase()] ||
  CapitalizeEachWord(status || "");

const isPreOrder = (row) =>
  (row.businessType ?? row.orderType ?? "").toUpperCase() === PRE_ORDER_TYPE;

const isPurchaseRequest = (row) =>
  (row.businessType ?? row.orderType ?? "").toUpperCase() ===
  "PURCHASE_REQUEST";

const getStatusStyle = (label, isActionRequired) => {
  const key = label.toLowerCase();
  if (key === "created" && isActionRequired) {
    return STATUS_STYLES["created-action"];
  }
  return STATUS_STYLES[key] || { color: "#4d4d4d", bgcolor: "#EDEDED" };
};

const StatusCell = ({
  status,
  isActionRequired,
  canadianPreorder,
  isLincoln,
}) => {
  const { t } = useTranslation("emp");
  if (!status) {
    return null;
  }

  const label = mapStatusLabel(status);
  const { color, bgcolor } = getStatusStyle(
    label,
    isActionRequired && !canadianPreorder
  );

  return (
    <Box
      data-testid="order-status"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        px: 1.5,
        py: 0.25,
        borderRadius: "4px",
        bgcolor,
        ...(canadianPreorder
          ? { px: "6px", py: "2px", borderRadius: "3px" }
          : {}),
      }}
    >
      <Typography
        variant="body2"
        component="span"
        sx={{
          color,
          fontWeight: 700,
          ...(canadianPreorder ? { fontSize: 12, lineHeight: "16px" } : {}),
          whiteSpace: "nowrap",
          ...(isLincoln && {
            fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
            fontWeight: 600,
            letterSpacing: 0,
          }),
        }}
      >
        {canadianPreorder && ["Created", "Accepted", "Canceled"].includes(label)
          ? t(`CanadianPreorderOrders.filters.${label.toLowerCase()}`, {
              defaultValue: label,
            })
          : label}
      </Typography>
    </Box>
  );
};

StatusCell.propTypes = {
  status: PropTypes.string,
  isActionRequired: PropTypes.bool,
  canadianPreorder: PropTypes.bool,
  isLincoln: PropTypes.bool,
};

StatusCell.defaultProps = {
  status: "",
  isActionRequired: false,
  canadianPreorder: false,
  isLincoln: false,
};

// "--" is the placeholder the default columns already use for missing data
const isEmptyContent = (content) =>
  content === null ||
  content === undefined ||
  (typeof content === "string" && ["", "--"].includes(content.trim()));

const renderSimpleCell = (value) => (
  <Typography variant="body2" component="p">
    {value}
  </Typography>
);

const EMPOrderTable = ({
  rows,
  columns: customColumns,
  hiddenColumns,
  emptyCellText,
  canadianPreorder,
  fixedLayout,
}) => {
  const [copiedField, setCopiedField] = useState({
    rowId: null,
    columnId: null,
  });

  const queryClient = useQueryClient();
  const { user } = useContext(UserContext);
  const { t } = useTranslation("emp");
  const isLincoln = user?.brand === "L";
  const isCanadianPreorder = canadianPreorder && isCanadianPreorderUser(user);
  const getHeaderLabel = useCallback(
    (key, defaultValue) =>
      isCanadianPreorder
        ? t(`CanadianPreorderOrders.headers.${key}`, { defaultValue })
        : defaultValue,
    [isCanadianPreorder, t]
  );

  const commonId = useMemo(
    () =>
      user
        ? `${user.market}|${user.dealerId}|${user.salesCode}|${user.brand}`
        : "",
    [user]
  );

  const handleCopy = useCallback(
    async (value, rowId, columnId) => {
      try {
        await navigator.clipboard.writeText(value);
        setCopiedField({ rowId, columnId });

        OrdersPageAnalytics.trackCopyAction(columnId, user);

        setTimeout(() => {
          setCopiedField({ rowId: null, columnId: null });
        }, 2000);
      } catch (err) {
        console.error("Failed to copy text: ", err);
      }
    },
    [user]
  );

  const allColumns = useMemo(
    () =>
      customColumns ??
      (isLincoln
        ? [
            {
              id: "orderStatus",
              label: getHeaderLabel("status", "Status"),
              render: (row) => (
                <StatusCell
                  status={row.orderStatus || "Unknown"}
                  isActionRequired={row.isActionRequired}
                  isLincoln
                />
              ),
            },
            {
              id: "orderIdString",
              label: getHeaderLabel("orderNumber", "Order #"),
              copy: true,
            },
            {
              id: "orderDate",
              label: getHeaderLabel("submittedDate", "Submitted Date"),
              render: (row) =>
                row.orderDate
                  ? UiDateUtils.formatDateMmDdYyyy(new Date(row.orderDate))
                  : "--",
            },
            {
              id: "customerName",
              label: isCanadianPreorder
                ? getHeaderLabel("customerName", "Customer Name")
                : t("CustomerOrders.ordersTableCustomerName"),
              wrap: true,
              render: (row) => CapitalizeEachWord(row.customerName),
            },
            {
              id: "vin",
              label: getHeaderLabel("vin", "VIN"),
              value: (row) => row.vin || (isPreOrder(row) ? "Preorder" : "--"),
              copy: (row) => Boolean(row.vin),
            },
            {
              id: "delivery",
              label: getHeaderLabel(
                "estimatedDeliveryDate",
                "Est. Delivery Date"
              ),
              permission: isCanadianPreorder
                ? undefined
                : "preorderEstDelivery",
              render: (row) => {
                if (/cancel/i.test(row.orderStatus || "")) {
                  return "Canceled";
                }
                const edd = row.estimatedDeliveryDate ?? row.delivery;
                return isPurchaseRequest(row)
                  ? (formatPurchaseRequestEta(edd, t) ?? "--")
                  : (edd ?? "--");
              },
            },
            {
              id: "actions",
              label: getHeaderLabel("details", "Details"),
              render: (row) => (
                <LinkToDetails
                  orderIdString={row.orderIdString}
                  queryClient={queryClient}
                  commonId={commonId}
                  user={user}
                  isPreOrder={isPreOrder(row)}
                />
              ),
            },
          ]
        : [
            {
              id: "status",
              label: getHeaderLabel("status", "Status"),
              render: (row) =>
                row.orderStatus ? (
                  <StatusCell
                    status={row.orderStatus}
                    isActionRequired={row.isActionRequired}
                    canadianPreorder={isCanadianPreorder}
                  />
                ) : null,
            },
            {
              id: "orderIdString",
              label: getHeaderLabel("orderNumber", "Order #"),
              sortable: true,
              copy: true,
            },
            {
              id: "orderDate",
              label: getHeaderLabel("submittedDate", "Submitted Date"),
              sortable: true,
              render: (row) => {
                const date = new Date(row.orderDate);
                if (Number.isNaN(date.getTime())) {
                  return "";
                }
                return `${date.getMonth() + 1}/${date
                  .getDate()
                  .toString()
                  .padStart(2, "0")}/${date.getFullYear()}`;
              },
            },
            {
              id: "customerName",
              label: getHeaderLabel("customerName", "Customer Name"),
              sortable: true,
              copy: false,
              wrap: true,
              render: (row) => CapitalizeEachWord(row.customerName),
            },
            {
              id: "vin",
              label: getHeaderLabel("vin", "VIN"),
              wrap: true,
              value: (row) => {
                if (isCanadianPreorder) {
                  return (
                    row.vin ||
                    (isPreOrder(row)
                      ? t("CanadianPreorderOrders.preOrder", {
                          defaultValue: "Preorder",
                        })
                      : row.vin)
                  );
                }
                return isPreOrder(row) ? PRE_ORDER_NO_VIN_TEXT : row.vin;
              },
              copy: (row) =>
                (isCanadianPreorder || !isPreOrder(row)) && Boolean(row.vin),
            },
            {
              id: "estimatedDeliveryDate",
              label: getHeaderLabel(
                "estimatedDeliveryDate",
                "Est. Delivery Date"
              ),
              permission: isCanadianPreorder
                ? undefined
                : "preorderEstDelivery",
              render: (row) => {
                const edd = row.estimatedDeliveryDate ?? row.delivery;
                const value = isPurchaseRequest(row)
                  ? formatPurchaseRequestEta(edd, t)
                  : edd;
                return typeof value === "string" && value
                  ? renderSimpleCell(value)
                  : null;
              },
            },
            {
              id: "actions",
              label: getHeaderLabel("details", "Details"),
              render: (row) => (
                <LinkToDetails
                  orderIdString={row.orderIdString}
                  queryClient={queryClient}
                  commonId={commonId}
                  user={user}
                  isPreOrder={isPreOrder(row)}
                  viewLabel={
                    isCanadianPreorder
                      ? t("CanadianPreorderOrders.view", {
                          defaultValue: "View",
                        })
                      : undefined
                  }
                />
              ),
            },
          ]),
    [
      queryClient,
      commonId,
      user,
      isLincoln,
      customColumns,
      isCanadianPreorder,
      getHeaderLabel,
      t,
    ]
  );

  const columns = useMemo(
    () => allColumns.filter((column) => !hiddenColumns.includes(column.id)),
    [allColumns, hiddenColumns]
  );

  const fixedTableMinWidth = columns
    .filter(
      (column) =>
        !column.permission ||
        hasPermissionTo(user?.permissions?.rolePermissions, [column.permission])
    )
    .reduce(
      (width, column) => width + (FIXED_COLUMN_WIDTHS[column.id] ?? 220),
      0
    );

  const renderCellContent = useCallback(
    (column, row) => {
      const hasPlaceholder = emptyCellText !== undefined;

      if (column.render) {
        const content = column.render(row);
        return hasPlaceholder && isEmptyContent(content)
          ? renderSimpleCell(emptyCellText)
          : content;
      }

      const value =
        typeof column.value === "function" ? column.value(row) : row[column.id];

      if (hasPlaceholder && isEmptyContent(value)) {
        return renderSimpleCell(emptyCellText);
      }

      if (column.copy) {
        const isCopyEnabled =
          typeof column.copy === "function" ? column.copy(row) : column.copy;

        if (!isCopyEnabled) {
          return renderSimpleCell(value);
        }

        const isCopied =
          copiedField.rowId === row.orderIdString &&
          copiedField.columnId === column.id;

        return (
          <CopyableCell
            value={value}
            rowId={row.orderIdString}
            columnId={column.id}
            isCopied={isCopied}
            isLincoln={isLincoln}
            onCopy={handleCopy}
          />
        );
      }

      return renderSimpleCell(value);
    },
    [copiedField, handleCopy, emptyCellText, isLincoln]
  );

  const withColumnPermission = (column, renderCell) =>
    column.permission ? (
      <HasPermissionTo
        key={column.id}
        perform={[column.permission]}
        permissions={user?.permissions?.rolePermissions}
        render={renderCell}
      />
    ) : (
      renderCell()
    );

  return (
    <TableContainer
      component={Paper}
      sx={{ boxShadow: "none", borderRadius: 0 }}
    >
      <Table
        sx={(theme) => ({
          minWidth: isLincoln || isCanadianPreorder ? undefined : 650,
          width: "100%",
          ...(isLincoln
            ? {
                tableLayout: "auto",
                fontFamily: "ProximaNovaRegular, Arial, sans-serif",
                "& th, & td": {
                  height: "56px",
                  boxSizing: "border-box",
                  fontSize: theme.typography.body2.fontSize,
                  lineHeight: "18px",
                  color: "#444",
                  borderBottom: "0.25px solid #ededed",
                },
                "& td": { typography: "body2" },
                "& td p": {
                  typography: "body2",
                  letterSpacing: 0,
                },
                "& th .MuiTypography-root": {
                  fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
                  fontSize: "12px",
                  lineHeight: "16px",
                  fontWeight: 600,
                  letterSpacing: 0,
                },
                "& td > div": { gap: "8px" },
                "& td.wrap-cell, & td.wrap-cell p": {
                  whiteSpace: "normal",
                  overflowWrap: "break-word",
                },
                "& td:last-of-type, & th:last-of-type": { textAlign: "center" },
                "& a": {
                  color: "#485b65",
                  textDecoration: "none",
                  fontWeight: 600,
                  fontSize: theme.typography.body2.fontSize,
                  lineHeight: theme.typography.body2.lineHeight,
                },
                "& a:hover": { textDecoration: "underline" },
                "& a:focus-visible": {
                  outline: "2px solid #485b65",
                  outlineOffset: 3,
                },
                // Wide screens have room for larger text without scrolling
                [theme.breakpoints.up("lg")]: {
                  "& td, & td .MuiTypography-root, & td a": {
                    fontSize: theme.typography.body1.fontSize,
                    lineHeight: theme.typography.body1.lineHeight,
                  },
                  "& th .MuiTypography-root": {
                    fontSize: "14px",
                    lineHeight: "20px",
                  },
                },
              }
            : {}),
          ...(isCanadianPreorder
            ? {
                tableLayout: "auto",
                fontFamily: theme.typography.body2.fontFamily,
                "& th, & td": {
                  height: "48px",
                  boxSizing: "border-box",
                  overflowWrap: "anywhere",
                },
                "& td p": { minWidth: 0 },
                "& td > div": { gap: theme.spacing(2) },
                "& td button": { flexShrink: 0 },
                "& a": { color: "#066fef", textDecoration: "none" },
                "& a:hover": { textDecoration: "underline" },
                "& a:focus-visible": {
                  outline: "2px solid #066fef",
                  outlineOffset: 3,
                },
              }
            : {}),
          ...(fixedLayout
            ? {
                tableLayout: "fixed",
                minWidth: fixedTableMinWidth,
                "& thead th, & tbody td": {
                  whiteSpace: "nowrap",
                },
                "& td:not(.wrap-cell) .MuiTypography-root": {
                  whiteSpace: "nowrap",
                },
                "& td.wrap-cell, & td.wrap-cell .MuiTypography-root": {
                  minWidth: 0,
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                },
              }
            : {}),
        })}
        aria-label={isLincoln ? "Customer handling orders" : "simple table"}
      >
        {fixedLayout && (
          <colgroup>
            {columns.map((column) =>
              withColumnPermission(column, () => (
                <col
                  key={column.id}
                  style={{
                    width: `${
                      ((FIXED_COLUMN_WIDTHS[column.id] ?? 220) /
                        fixedTableMinWidth) *
                      100
                    }%`,
                  }}
                />
              ))
            )}
          </colgroup>
        )}
        <TableHead
          sx={{
            borderBottom: isLincoln
              ? "0.25px solid #b2b2b2"
              : "1px solid #b2b2b2",
            ...(isLincoln
              ? { "& th": { borderBottom: "0.25px solid #b2b2b2" } }
              : {}),
          }}
        >
          <TableRow>
            {columns.map((column) =>
              withColumnPermission(column, () => (
                <TableCell
                  key={column.id}
                  sx={{
                    whiteSpace:
                      !fixedLayout && isCanadianPreorder ? "normal" : "nowrap",
                    maxWidth: "none",
                    ...(isCanadianPreorder ? { py: 2, px: 3 } : {}),
                    ...(isLincoln
                      ? {
                          ...LINCOLN_CELL_PADDING,
                          borderBottom: "0.25px solid #b2b2b2",
                        }
                      : {}),
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: "#666" }}>
                    {column.label}
                  </Typography>
                </TableCell>
              ))
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.orderIdString}
              hover
              sx={{
                "&:first-of-type td:not(.wrap-cell)":
                  fixedLayout || isCanadianPreorder
                    ? {}
                    : { whiteSpace: "nowrap", minWidth: "24px" },
                "&:last-child td, &:last-child th": isLincoln
                  ? {}
                  : { border: 0 },
                "&:nth-of-type(odd)": {
                  backgroundColor: isLincoln ? "#fff" : "#fafafa",
                },
              }}
            >
              {columns.map((column) =>
                withColumnPermission(column, () => (
                  <TableCell
                    key={column.id}
                    className={column.wrap ? "wrap-cell" : undefined}
                    sx={{
                      whiteSpace:
                        column.wrap || (!fixedLayout && isCanadianPreorder)
                          ? "normal"
                          : "nowrap",
                      maxWidth: "none",
                      ...(isCanadianPreorder ? { py: 2, px: 3 } : {}),
                      ...(isLincoln
                        ? {
                            ...LINCOLN_CELL_PADDING,
                            borderBottom: "0.25px solid #ededed",
                          }
                        : {}),
                    }}
                  >
                    {renderCellContent(column, row)}
                  </TableCell>
                ))
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

EMPOrderTable.propTypes = {
  rows: PropTypes.arrayOf(orderRowPropType),
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      copy: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
      wrap: PropTypes.bool,
      permission: PropTypes.string,
      render: PropTypes.func,
      value: PropTypes.func,
    })
  ),
  hiddenColumns: PropTypes.arrayOf(PropTypes.string),
  emptyCellText: PropTypes.string,
  canadianPreorder: PropTypes.bool,
  fixedLayout: PropTypes.bool,
};

EMPOrderTable.defaultProps = {
  rows: [],
  columns: undefined,
  hiddenColumns: [],
  emptyCellText: undefined,
  canadianPreorder: false,
  fixedLayout: false,
};

export default EMPOrderTable;
